import { useMemo } from 'react';
import {
    DealerQuotesAction,
    DealerQuotesStatus,
    FullQuotationDataFragment,
    LiveDealerQuotesDataFragment,
} from '../../../../../api';
import useInviteQuoteData from './useInviteQuoteData';

type UseDealerQuotesProps = {
    quotation: FullQuotationDataFragment | LiveDealerQuotesDataFragment;
};

export type DealerQuote = {
    id: string;
    name: string;
    tagNames: string | null;
    status: DealerQuotesStatus;
    amount?: number | null;
    createdOn: Date;
    invitedOn?: Date;
    reinvitedOn?: Date;
    placedOn?: Date;
    actionHistory: {
        action: DealerQuotesAction;
        actionOn: Date;
    }[];
};

type DealerInfoWithActionHistory = {
    actionHistory?:
        | {
              action: DealerQuotesAction;
              actionOn: Date | string;
          }[]
        | null;
};

export const useDealerQuotes = ({ quotation }: UseDealerQuotesProps) => {
    const { dealers, tags } = useInviteQuoteData();

    const dealerQuotes = useMemo<DealerQuote[]>(() => {
        const getLatestActionOn = (dealerInfo: DealerInfoWithActionHistory, action: DealerQuotesAction) =>
            dealerInfo.actionHistory
                ?.filter(history => history.action === action)
                .sort((a, b) => new Date(b.actionOn).getTime() - new Date(a.actionOn).getTime())[0]?.actionOn;

        const taggedDealerNames = new Map<string, string[]>();
        const tagsInQuotation = tags.filter(tag => (quotation.tagIds ?? []).some(tagId => tagId === tag.id));
        const getTagNames = (userId: string) => {
            const tagNames = taggedDealerNames.get(userId) ?? [];
            tagsInQuotation
                .filter(tag => tag.userIds.includes(userId))
                .forEach(tag => {
                    tagNames.push(tag.name);
                    taggedDealerNames.set(userId, tagNames);
                });

            return tagNames.length ? tagNames.join(', ') : null;
        };

        const getQuoteAmount = (userId: string): { amount: number; placedOn: Date } | null => {
            const latestQuote = quotation?.entries
                ?.filter(entry => entry.dealerId === userId)
                .sort((a, b) => new Date(b.placedOn).getTime() - new Date(a.placedOn).getTime())[0];

            return latestQuote?.amount != null
                ? {
                      amount: latestQuote.amount,
                      placedOn: new Date(latestQuote.placedOn),
                  }
                : null;
        };

        return (quotation.dealersInfo ?? [])
            .map(dealerInfo => ({
                id: dealerInfo.dealerId,
                name: dealers.find(dealer => dealer.id === dealerInfo.dealerId)?.fullName ?? '',
                tagNames: getTagNames(dealerInfo.dealerId),
                status: dealerInfo.status,
                createdOn: new Date(getLatestActionOn(dealerInfo, DealerQuotesAction.Created) ?? quotation.createdOn),
                invitedOn: getLatestActionOn(dealerInfo, DealerQuotesAction.Invited)
                    ? new Date(getLatestActionOn(dealerInfo, DealerQuotesAction.Invited) as string | Date)
                    : undefined,
                reinvitedOn: getLatestActionOn(dealerInfo, DealerQuotesAction.Reinvited)
                    ? new Date(getLatestActionOn(dealerInfo, DealerQuotesAction.Reinvited) as string | Date)
                    : undefined,
                ...getQuoteAmount(dealerInfo.dealerId),
                actionHistory:
                    dealerInfo.actionHistory?.map(history => ({
                        action: history.action,
                        actionOn: new Date(history.actionOn),
                    })) ?? [],
            }))
            .sort((a, b) => {
                if (a.amount == null) {
                    return b.amount == null ? 0 : 1;
                }

                if (b.amount == null) {
                    return -1;
                }

                return b.amount - a.amount || (a.placedOn?.getTime() ?? 0) - (b.placedOn?.getTime() ?? 0);
            });
    }, [tags, quotation.dealersInfo, quotation.tagIds, quotation?.entries, quotation.createdOn, dealers]);

    return dealerQuotes;
};
